import styled, { StyledComponent } from "@emotion/styled";
import { DetailedHTMLProps, HTMLAttributes } from "react";
import { ttNormsFontFamily, ttNormsVarFontFamily } from "../../../fonts/fonts";
import { typeRampLodestarDefinitions } from "../constants/lodestarConstants";
import { ursaTypeRampDefinitions } from "../constants/ursaConstants";
import { TypeRampLodestarProps, TypeRampUrsaProps } from "../types";
import { TypeRampComponentBaseProps, TypeRampComponentBase } from "./TypeRampComponentBase";

export type SystemAndVariant =
    | Pick<TypeRampLodestarProps, "variant" | "system">
    | Pick<TypeRampUrsaProps, "variant" | "system">;

type TypeRampV2VariantKeys =
    | `${TypeRampLodestarProps["system"]}_${TypeRampLodestarProps["variant"]}`
    | `${TypeRampUrsaProps["system"]}_${TypeRampUrsaProps["variant"]}`;

function getKey({ system, variant }: SystemAndVariant): TypeRampV2VariantKeys {
    switch (system) {
        case "lodestar":
            return `${system}_${variant}`;
        case "ursa":
            return `${system}_${variant}`;
    }
}

const typeRampV2PrecomputedComponents = {} as Record<
    TypeRampV2VariantKeys,
    StyledComponent<DetailedHTMLProps<HTMLAttributes<HTMLElement>, HTMLElement>, TypeRampComponentBaseProps, object>
>;

// Pre-calc the Ursa variants as styled components. This is around a 10x performance improvement
// on trying to use the css prop or generating them on the fly.
for (const [key, { fontFamily, ...styles }] of Object.entries(ursaTypeRampDefinitions)) {
    const Component = styled(TypeRampComponentBase)({
        ...styles,
        ...(fontFamily === ttNormsFontFamily
            ? {
                  fontFamily: ttNormsFontFamily,
                  ["@supports (font-variation-settings: normal)"]: {
                      fontFamily: ttNormsVarFontFamily,
                  },
              }
            : {}),
    })
        // Without this, the React typings aren't happy. It REALLY wants to make sure it's a span.
        .withComponent("span");
    typeRampV2PrecomputedComponents[`ursa_${key as keyof typeof ursaTypeRampDefinitions}`] = Component;
}

// Pre-calc the Lodestar variants as styled components. This is around a 10x performance improvement
// on trying to use the css prop or generating them on the fly.
for (const [key, { fontFamily, ...styles }] of Object.entries(typeRampLodestarDefinitions)) {
    const Component = styled(TypeRampComponentBase)({
        ...styles,
        ...(fontFamily === "TT Norms Pro"
            ? {
                  fontFamily: ttNormsFontFamily,
                  ["@supports (font-variation-settings: normal)"]: {
                      fontFamily: ttNormsVarFontFamily,
                  },
              }
            : {}),
    })
        // Without this, the React typings aren't happy. It REALLY wants to make sure it's a span.
        .withComponent("span");
    typeRampV2PrecomputedComponents[`lodestar_${key as keyof typeof typeRampLodestarDefinitions}`] = Component;
}

export const getTypeRampV2PrecomputedComponent = (systemAndVariant: SystemAndVariant) => {
    const key = getKey(systemAndVariant);
    return typeRampV2PrecomputedComponents[key];
};
